import type {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {
  getOrgId,
  getProjectId,
  isCurrentProjectInternal,
  isUrlDefinedExperimentalFeatureEnabled,
} from 'common/report/util';
import {isDevEnv, isTestEnv} from 'common/report/util/env';

import {FeatureGate, ProjectFeatureFlag} from '../constants';

export const featureSamplingUtils = {
  determineFeatureEligibility,
};

/**
 * Check deterministically whether a user or group is included in a feature rollout/test based on a
 * unique user/group string identifier and the desired percentage to include.
 */
function determineFeatureEligibility(
  userOrGroupId: string,
  feature: string,
  percentAllowed: number /* must be in range [0, 100] */,
): boolean {
  if (!(percentAllowed >= 0 && percentAllowed <= 100)) {
    throw new Error(`@percentAllowed argument must be in range [0, 100] inclusive`);
  }

  if (typeof userOrGroupId === `undefined` || userOrGroupId === null || userOrGroupId === ``) {
    throw new Error(`@userOrGroupId argument cannot be null or undefined or empty`);
  }
  if (typeof feature === `undefined` || feature === null || feature === ``) {
    throw new Error(`@feature argument cannot be null or undefined or empty`);
  }

  switch (percentAllowed) {
    case 0:
      return false;
    case 100:
      return true;
    default:
      break;
  }

  const key = userOrGroupId + feature;

  // Bernstein's hash: http://www.cse.yorku.ca/~oz/hash.html#djb2
  // The djb2 hash was chosen because of the claim that "it has excellent distribution and speed on many different sets of keys and table sizes",
  // but we have not validated whether it is statistically reliable and accurate to use in sampling users/groups for rollouts and analysis.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) + hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  const dart = (hash >>> 0) % 100;
  return dart < percentAllowed;
}

enum FeatureRolloutType {
  Project,
  Org,
}
class FeatureRollout {
  private type: FeatureRolloutType;
  private percentage: number;
  private featureflag: ProjectFeatureFlag;
  private explicitlyDisallowIds: string[] = [];

  public name: FeatureRolloutName;

  constructor(params: {
    type: FeatureRolloutType;
    name: FeatureRolloutName;
    percentage: number;
    featureflag?: ProjectFeatureFlag;
    explicitlyDisallowIds?: string[];
  }) {
    this.type = params.type;
    this.name = params.name;
    this.percentage = params.percentage;
    this.featureflag = params.featureflag;
    this.explicitlyDisallowIds = params.explicitlyDisallowIds ?? [];
  }

  isEnabled(project: CurrentProject | ProjectDetails | null): boolean {
    if (this.type === FeatureRolloutType.Project) {
      const projectId = project?.id.toString() ?? getProjectId();
      if (this.explicitlyDisallowIds.includes(projectId)) {
        return false;
      }
      return (
        featureSamplingUtils.determineFeatureEligibility(projectId, this.name.toString(), this.percentage) ||
        project?.hasFeatureFlag(this.featureflag)
      );
    } else {
      const orgId = project?.organizationId.toString();
      if (this.explicitlyDisallowIds.includes(orgId)) {
        return false;
      }
      return orgId
        ? featureSamplingUtils.determineFeatureEligibility(orgId, this.name.toString(), this.percentage) ||
            project?.hasFeatureFlag(this.featureflag)
        : false;
    }
  }
}

// Add any gradual feature rollouts here
export enum FeatureRolloutName {
  CleanerUrls = `cleaner-urls`,
  DashboardUndoRedo = `dashboard-undo-redo`,
  InsightsPivotTable = `insights-pivot-table`,
  OnboardingChecklist = `onboarding-checklist`,
  InsightsNewCountMenu = `insights-new-count-menu`,
  CartAnalysis = `cart-analysis`,
  BetterBrokenState = `better-broken-state`,
  MultiAttribution = `multi-touch-attribution`,
  MultiAttributionFunnelsRetention = `multi-touch-attribution-funnels-retention`,
  UniqueUniques = `unique-uniques`,
  RHSMode = `rhs-mode`,
  Home = `home`,
  PageDuration = `page-duration`,
  WebAnalyticsDashboardAndChannelClassifier = `web-analytics-dashboard-and-channel-classifier`,
  BenchmarkTemplate = `benchmark-template`,
  WarehouseLaunchTemplates = `wh_launch_templates`,
  SingleReport = `single_report`,
  ImmersiveOnboardingPhase101 = `immersive-onboarding-phase-1`,
}

export const FeatureRollouts: {[key in FeatureRolloutName]: FeatureRollout} = {
  [FeatureRolloutName.CleanerUrls]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CleanerUrls,
    percentage: 100,
  }),
  [FeatureRolloutName.DashboardUndoRedo]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.DashboardUndoRedo,
    percentage: 100,
  }),
  [FeatureRolloutName.InsightsPivotTable]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.InsightsPivotTable,
    percentage: 0,
  }),
  [FeatureRolloutName.OnboardingChecklist]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.OnboardingChecklist,
    percentage: 0,
  }),
  [FeatureRolloutName.InsightsNewCountMenu]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.InsightsNewCountMenu,
    featureflag: ProjectFeatureFlag.InsightsNewCountMenu,
    percentage: 0,
  }),
  [FeatureRolloutName.CartAnalysis]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CartAnalysis,
    featureflag: ProjectFeatureFlag.CartAnalysis,
    percentage: 100,
  }),
  [FeatureRolloutName.BetterBrokenState]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.BetterBrokenState,
    featureflag: ProjectFeatureFlag.BetterBrokenState,
    percentage: 100,
  }),
  [FeatureRolloutName.MultiAttribution]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.MultiAttribution,
    featureflag: ProjectFeatureFlag.MultiAttribution,
    percentage: 100,
  }),
  [FeatureRolloutName.MultiAttributionFunnelsRetention]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.MultiAttributionFunnelsRetention,
    featureflag: ProjectFeatureFlag.MultiAttributionFunnelsRetention,
    percentage: 100,
  }),
  [FeatureRolloutName.UniqueUniques]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.UniqueUniques,
    featureflag: ProjectFeatureFlag.UniqueUniques,
    percentage: 100,
  }),
  [FeatureRolloutName.RHSMode]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.RHSMode,
    percentage: 100, // TODO(RHS): tweak this on launch
  }),
  [FeatureRolloutName.Home]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.Home,
    percentage: 0,
  }),
  [FeatureRolloutName.WebAnalyticsDashboardAndChannelClassifier]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.WebAnalyticsDashboardAndChannelClassifier,
    featureflag: ProjectFeatureFlag.WebAnalyticsDashboard,
    percentage: 100,
  }),
  [FeatureRolloutName.PageDuration]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.PageDuration,
    featureflag: ProjectFeatureFlag.PageDuration,
    percentage: 100,
  }),
  [FeatureRolloutName.BenchmarkTemplate]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.BenchmarkTemplate,
    percentage: 0,
  }),
  [FeatureRolloutName.WarehouseLaunchTemplates]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.WarehouseLaunchTemplates,
    percentage: 0,
    featureflag: ProjectFeatureFlag.WarehouseLaunchTemplates,
  }),
  [FeatureRolloutName.SingleReport]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.SingleReport,
    percentage: 50,
    featureflag: ProjectFeatureFlag.SingleReport,
  }),
  [FeatureRolloutName.ImmersiveOnboardingPhase101]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.ImmersiveOnboardingPhase101,
    percentage: 5,
  }),
};

const REPORT_CONSISTENCY_EXCLUDE_ORGS = [];

export function useReportConsistency() {
  return isUrlDefinedExperimentalFeatureEnabled(window.location, `report_consistency`, () => {
    const projectId = getProjectId();
    const orgId = getOrgId(projectId);
    if (!orgId) {
      return true;
    } else if (REPORT_CONSISTENCY_EXCLUDE_ORGS.includes(orgId)) {
      return false;
    }

    return true;
  });
}

export function useReportConsistencyBanner() {
  return isUrlDefinedExperimentalFeatureEnabled(window.location, `report_consistency_banner`, () => {
    const projectId = getProjectId();
    const orgId = getOrgId(projectId);
    if (!orgId) {
      return false;
    } else if (REPORT_CONSISTENCY_EXCLUDE_ORGS.includes(orgId)) {
      return true;
    }

    return false;
  });
}

export function useTemplateSearch() {
  if (isTestEnv()) {
    return true;
  }
  return isUrlDefinedExperimentalFeatureEnabled(window.location, `template_search`, () => isCurrentProjectInternal());
}

export function isFoundationalTemplatesEnabled(currentProject: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  const isRolloutEnabled = featureSamplingUtils.determineFeatureEligibility(
    currentProject.organizationId.toString(),
    ProjectFeatureFlag.FoundationalTemplates,
    100, // @JakeW update rollout % for Foundational Templates here
  );
  return isRolloutEnabled || currentProject.hasFeatureFlag(ProjectFeatureFlag.FoundationalTemplates);
}

export function isRHSModeEnabled(currentProject: CurrentProject, isBenchmarkTemplate: boolean): boolean {
  return (
    isUrlDefinedExperimentalFeatureEnabled(window.location, `rhs-mode`, true) ||
    FeatureRollouts[FeatureRolloutName.RHSMode].isEnabled(currentProject) ||
    isBenchmarkTemplate
  );
}

export function isBenchmarkTemplateEnabled(currentProject: CurrentProject): boolean {
  return (
    isUrlDefinedExperimentalFeatureEnabled(
      window.location,
      FeatureRolloutName.BenchmarkTemplate,
      true /** Enable for internal projects */,
    ) || FeatureRollouts[FeatureRolloutName.BenchmarkTemplate].isEnabled(currentProject)
  );
}

export function shouldShowHomePage(currentProject: CurrentProject): boolean {
  // TODO(Home): Clean up after Home MVP
  return (
    isUrlDefinedExperimentalFeatureEnabled(window.location, `home-prototype`, true) ||
    FeatureRollouts[FeatureRolloutName.Home].isEnabled(currentProject)
  );
}

export const useInviteV2 = (orgId) => {
  return isDevEnv() || orgId === 1 || featureSamplingUtils.determineFeatureEligibility(orgId, `invite-modal-v2`, 50);
};

export function isDataVolumeMonitoringEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return currentProject.getFeatureGate(FeatureGate.DataGovernance);
}

export function isSessionRecordingEnabled(currentProject?: CurrentProject) {
  if (!currentProject) {
    return false;
  }
  return currentProject.hasFeatureFlag(ProjectFeatureFlag.SessionRecording);
}

export function isImmersiveOnboardingPhase101Enabled(currentProject: CurrentProject): boolean {
  return (
    isUrlDefinedExperimentalFeatureEnabled(
      window.location,
      FeatureRolloutName.ImmersiveOnboardingPhase101,
      true /** Enable for internal projects */,
    ) || FeatureRollouts[FeatureRolloutName.ImmersiveOnboardingPhase101].isEnabled(currentProject)
  );
}
